// Public careers page. No auth — anyone on the internet can browse jobs.
//
// Pulls /api/v1/careers/jobs (no PII, no internal notes). Filters by
// profession, state, and free-text search. Click a job → /careers/:id.
//
// Branded per tenant (white-label) via the public branding endpoint. No app
// sidebar or internal navigation — completely standalone, like a marketing page.
//
// Phase 2 "The Look": token-driven + theme-aware (honors the visitor's dark
// mode), mobile-first (candidates browse from a phone), and the tenant's
// brand color drives the accent via --brand-primary. Built on the UI kit.

import { useEffect, useMemo, useState, CSSProperties } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { usePublicBranding, CAREERS_EMAIL } from '../lib/publicBranding';
import PublicGhlChat from '../components/PublicGhlChat';
import { Card, Field, Input, Select, Button, Icon, EmptyState } from '../components/ui';
import type { IconName } from '../components/ui';

interface PublicJob {
  id: string;
  job_code: string | null;
  title: string;
  profession: string | null;
  specialty: string | null;
  sub_specialty: string | null;
  description: string | null;
  city: string | null;
  state: string | null;
  pay_summary: string | null;
  hours_per_week: number | null;
  shift: string | null;
  duration_weeks: number | null;
  start_date: string | null;
  posted_at: string;
  updated_at: string;
}

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined)
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

// Apply the tenant's brand color to this standalone tree so every --brand-*
// derived token (buttons, tints, accents) re-themes from one value.
export function brandVars(primary: string | null): CSSProperties | undefined {
  return primary ? ({ ['--brand-primary' as string]: primary } as CSSProperties) : undefined;
}

export default function Careers() {
  const [params, setParams] = useSearchParams();
  const brand = usePublicBranding();
  const [jobs, setJobs] = useState<PublicJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const q = params.get('q') ?? '';
  const profession = params.get('profession') ?? '';
  const state = params.get('state') ?? '';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    const qs = new URLSearchParams();
    if (q) qs.set('q', q);
    if (profession) qs.set('profession', profession);
    if (state) qs.set('state', state);
    fetch(`${API_BASE}/careers/jobs?${qs.toString()}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((data: { jobs: PublicJob[] }) => {
        // Server order is the board order. Do not sort here: correctional
        // roles are kept out of the first cards, and a client sort would
        // put a freshly imported jail job back on top.
        if (!cancelled) setJobs(data.jobs ?? []);
      })
      .catch(() => { if (!cancelled) setErr('Could not load open positions right now.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [q, profession, state]);

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const professions = useMemo(() => {
    const set = new Set<string>();
    jobs.forEach(j => { if (j.profession) set.add(j.profession); });
    return Array.from(set).sort();
  }, [jobs]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--sf2)', color: 'var(--t1)', ...brandVars(brand.primary_color) }}>
      <CareersHeader />

      {/* Brand-tinted hero */}
      <div style={{ background: 'linear-gradient(160deg, var(--brand-tint) 0%, transparent 68%)', borderBottom: '1px solid var(--bd)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 20px 32px' }}>
          <h1 style={{ margin: 0, fontSize: 'clamp(26px, 5vw, 34px)', fontWeight: 800, letterSpacing: -0.5, color: 'var(--t1)' }}>
            Find your next contract
          </h1>
          <p style={{ margin: '10px 0 0', fontSize: 15, color: 'var(--t2)', maxWidth: 600, lineHeight: 1.55 }}>
            {brand.company_name} helps clinicians find their next opportunity. Apply in two minutes,
            and get a real human reply within 24 hours.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 20px 80px' }}>
        {/* Filters — stack on phones, 3-up from tablet */}
        <div className="careers-filters" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 10, marginBottom: 20 }}>
          <Field label="Search" htmlFor="careers-q">
            <Input
              id="careers-q"
              value={q}
              onChange={(e) => setParam('q', e.target.value)}
              placeholder="Title, specialty, or city…"
              icon={<Icon name="search" size={16} />}
            />
          </Field>
          <Field label="Profession" htmlFor="careers-prof">
            <Select id="careers-prof" value={profession} onChange={(e) => setParam('profession', e.target.value)}>
              <option value="">All professions</option>
              {professions.map(p => <option key={p} value={p}>{p}</option>)}
            </Select>
          </Field>
          <Field label="State" htmlFor="careers-state">
            <Select id="careers-state" value={state} onChange={(e) => setParam('state', e.target.value)}>
              <option value="">All states</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
        </div>

        {err && (
          <div style={{ padding: 16, background: 'var(--danger-tint)', border: '1px solid var(--danger-border)', borderRadius: 'var(--r-md)', color: 'var(--danger-fg)', fontSize: 13, marginBottom: 16 }}>
            {err}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--t3)' }}>Loading positions…</div>
        ) : jobs.length === 0 ? (
          <Card>
            <EmptyState
              icon={<Icon name="search" size={26} />}
              title="No matching positions right now"
              description={
                <>Try clearing filters, or email{' '}
                  <a href={`mailto:${CAREERS_EMAIL}`} style={{ color: 'var(--pr)', fontWeight: 600 }}>{CAREERS_EMAIL}</a>{' '}
                  and tell us what you're looking for.
                </>
              }
            />
          </Card>
        ) : (
          <>
            <div style={{ fontSize: 13, color: 'var(--t3)', marginBottom: 12 }}>
              {jobs.length} open position{jobs.length === 1 ? '' : 's'}
            </div>
            <div style={{ display: 'grid', gap: 12 }}>
              {jobs.map(j => <JobCard key={j.id} job={j} />)}
            </div>
          </>
        )}
      </div>

      <CareersFooter />
      <PublicGhlChat
        primaryColor={brand.primary_color}
        locationId={brand.ghl_chat_location_id}
        widgetId={brand.ghl_chat_widget_id}
      />

      <style>{`
        @media (min-width: 640px) {
          .careers-filters { grid-template-columns: minmax(0, 1fr) 200px 140px !important; align-items: end; }
        }
      `}</style>
    </div>
  );
}

function FactChip({ icon, children }: { icon: IconName; children: React.ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--t2)' }}>
      <Icon name={icon} size={14} /> {children}
    </span>
  );
}

function JobCard({ job }: { job: PublicJob }) {
  const location = [job.city, job.state].filter(Boolean).join(', ');
  return (
    <Link
      to={`/careers/${job.id}`}
      style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
    >
      <Card interactive>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--t1)' }}>
              {job.title}
              {job.specialty && <span style={{ fontWeight: 500, color: 'var(--t2)' }}> · {job.specialty}</span>}
            </h3>
            <div style={{ marginTop: 8, display: 'flex', gap: 16, rowGap: 6, flexWrap: 'wrap' }}>
              {location && <FactChip icon="building">{location}</FactChip>}
              {job.profession && <FactChip icon="user">{job.profession}</FactChip>}
              {job.shift && <FactChip icon="clock">{job.shift}</FactChip>}
              {job.hours_per_week != null && <FactChip icon="pulse">{job.hours_per_week} hrs/wk</FactChip>}
              {job.duration_weeks != null && <FactChip icon="calendar">{job.duration_weeks} weeks</FactChip>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            {job.pay_summary && (
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--good-fg)' }}>{job.pay_summary}</div>
            )}
            <div style={{ marginTop: 4, fontSize: 11, color: 'var(--t3)' }}>
              Posted {new Date(job.posted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

export function CareersHeader() {
  const brand = usePublicBranding();
  return (
    <header style={{
      background: 'var(--surface)', color: 'var(--t1)', borderBottom: '1px solid var(--bd)',
      padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
      position: 'sticky', top: 0, zIndex: 10,
    }}>
      <Link to="/careers" style={{ color: 'var(--t1)', textDecoration: 'none', fontWeight: 800, fontSize: 16, letterSpacing: 0.2, display: 'inline-flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
        {brand.logo_url
          ? <img src={brand.logo_url} alt="" style={{ height: 24, width: 'auto', borderRadius: 4 }} />
          : <span style={{ display: 'inline-flex', width: 28, height: 28, borderRadius: 7, background: 'var(--brand-tint)', color: 'var(--pr)', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="building" size={17} /></span>}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{brand.company_name}</span>
      </Link>
      <Button href={`mailto:${CAREERS_EMAIL}`} variant="ghost" size="sm" icon={<Icon name="mail" size={15} />}>
        <span className="careers-contact-label">{CAREERS_EMAIL}</span>
      </Button>
      <style>{`@media (max-width: 480px){ .careers-contact-label{ display:none; } }`}</style>
    </header>
  );
}

export function CareersFooter() {
  const brand = usePublicBranding();
  const showPoweredBy = brand.app_name.trim().toLowerCase() !== brand.company_name.trim().toLowerCase();
  return (
    <footer style={{
      borderTop: '1px solid var(--bd)', padding: '20px 24px', background: 'var(--surface)',
      textAlign: 'center', color: 'var(--t3)', fontSize: 12,
    }}>
      © {new Date().getFullYear()} {brand.company_name}{showPoweredBy ? ` · Powered by ${brand.app_name}` : ''}
    </footer>
  );
}
